import { Col, Row } from 'antd'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown'
import { StatDisplayCount } from 'features/drilldown/components/stats/StatDisplayCount'
import { useFilteredEntryCount } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { GenericPanelData } from 'features/drilldown/types'
import { OntologyRolodexEntryType, useOntology } from 'features/dynamicOntology'
import React, { FC, useMemo } from 'react'
import { RolodexHistogramFilter } from 'types'

import { ScheduledInspectionCountView } from './CustomRenderers'

export type DrilldownCountConfig = {
  countType: string
  rolodexType: OntologyRolodexEntryType
  singularTitle: string
  pluralTitle: string
  filters?: ReadonlyArray<RolodexHistogramFilter>
}

export const COMPLETED_INSPECTION_FILTER: ReadonlyArray<RolodexHistogramFilter> =
  [
    {
      type: 'component-dataset',
      field: 'id',
      op: 'notnull',
    },
  ]

export const SPECIAL_COUNT_PANEL_CONFIGS: ReadonlyArray<DrilldownCountConfig> =
  [
    {
      countType: 'completedInspections',
      rolodexType: 'inspection',
      singularTitle: 'Inspections Completed',
      pluralTitle: 'Inspections Completed',
      filters: COMPLETED_INSPECTION_FILTER,
    },
    {
      countType: 'scheduledInspections',
      rolodexType: 'inspection',
      singularTitle: 'Inspections Scheduled',
      pluralTitle: 'Inspections Scheduled',
    },
  ]

export function useCountConfigs(): ReadonlyArray<DrilldownCountConfig> {
  const { ontology } = useOntology()
  return useMemo(
    () => [
      ...Object.entries(ontology.types).map(([key, typeDef]) => ({
        countType: `count_${key}`,
        rolodexType: key,
        singularTitle: typeDef.displayName,
        pluralTitle: typeDef.pluralName,
      })),
      ...Object.values(SPECIAL_COUNT_PANEL_CONFIGS),
    ],
    [ontology.types]
  )
}

export const COUNT_PANEL_CUSTOM_RENDERERS: Partial<
  Record<string, React.ReactNode>
> = {
  scheduledInspections: (
    <ScheduledInspectionCountView title='Scheduled Inspections' />
  ),
}

export interface CountsPanelData extends GenericPanelData {
  activeCountTypes: ReadonlyArray<string>
}

export const LATEST_COUNTS_PANEL_DATA_SCHEMA_VERSION = 3

export const DEFAULT_COUNTS_PANEL_DATA: CountsPanelData = {
  activeCountTypes: [
    'count_unit',
    'completedInspections',
    'scheduledInspections',
    'count_indication',
  ],
  panelDataSchemaVersion: LATEST_COUNTS_PANEL_DATA_SCHEMA_VERSION,
  title: 'Counts',
  isPinned: true,
}

const CountView: FC<{
  rolodexType: OntologyRolodexEntryType
  singularTitle: string
  pluralTitle: string
  filters?: ReadonlyArray<RolodexHistogramFilter>
}> = ({ rolodexType, filters, singularTitle, pluralTitle }) => {
  const { count, isLoading } = useFilteredEntryCount(rolodexType, filters)

  const displayTitle = useMemo(() => {
    return count === 1 ? singularTitle : pluralTitle
  }, [count, singularTitle, pluralTitle])

  return (
    <StatDisplayCount
      title={displayTitle}
      count={count}
      isLoading={isLoading}
      entryType={rolodexType}
    />
  )
}

export const DrilldownCountsView: FC = () => {
  const {
    panelData: { activeCountTypes },
  } = useVersionAwareDrilldownPanelData<CountsPanelData>()

  const countConfigs = useCountConfigs()

  return (
    <div style={{ padding: '16px' }}>
      <Row gutter={[16, 16]} justify='space-around' align='top'>
        {countConfigs
          .filter(
            (countConfig) =>
              countConfig && activeCountTypes.includes(countConfig.countType)
          )
          .map((countConfig) => (
            <Col
              key={countConfig.countType}
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={6}
              xxl={4}
              style={{ display: 'flex', justifyContent: 'center' }}>
              {COUNT_PANEL_CUSTOM_RENDERERS[countConfig.countType] ?? (
                <CountView
                  key={countConfig.countType}
                  singularTitle={countConfig.singularTitle}
                  pluralTitle={countConfig.pluralTitle}
                  rolodexType={countConfig.rolodexType}
                  filters={countConfig.filters}
                />
              )}
            </Col>
          ))}
      </Row>
    </div>
  )
}
